/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import {
	Avatar,
	Badge,
	Box,
	Button,
	Center,
	Checkbox,
	CheckboxGroup,
	Container,
	Divider,
	Flex,
	HStack,
	Progress,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Text,
	useDisclosure,
	useToast,
	WrapItem,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowAltRight, faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import TransferNoteOfPresentationItem from './TransferNoteOfPresentationItem';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import SelectUserModal from '../../components/Modal/SelectUserModal';
import useStore from '../../store';
import c from '../../constant';

const Q_USER = gql`
	query get($id: String!) {
		getUserById(id: $id) {
			id
			fullName
			fullNameKhmer
			avatar
		}
	}
`;

const Q_NPS = gql`
	query get($assigneeId: String!, $transfereeId: String!, $isAssistantAssignment: Boolean, $department: String, $office: String) {
		getAllNoteOfPresentationsAssignedToAssigneeId(
			assigneeId: $assigneeId
			transfereeId: $transfereeId
			isAssistantAssignment: $isAssistantAssignment
			department: $department
			office: $office
		)
	}
`;

const M_ASSIGN = gql`
	mutation assign($npId: String!, $assigneeId: String!, $isTransferAssistantAssignment: Boolean = false, $notification: Boolean = false) {
		assignNoteOfPresentationToAssigneeId(
			npId: $npId
			assigneeId: $assigneeId
			isTransferAssistantAssignment: $isTransferAssistantAssignment
			notification: $notification
		)
	}
`;

const CONCURRENCY = 8;
const PREVIEW_LIMIT = 50;
const FAILED_LIMIT = 200;

const DEPT_LABEL = new Map();
const OFFICE_LABEL = new Map();
c.departments.forEach((d) => {
	DEPT_LABEL.set(d.value, d.labelKh);
	d.offices?.forEach((o) => OFFICE_LABEL.set(o.value, o.labelKh));
});

function joinedBadges(csv, map, colorScheme) {
	if (!csv) return null;
	const labels = [];
	const parts = csv.split(',');
	for (let i = 0; i < parts.length; i++) {
		const label = map.get(parts[i]);
		if (label) labels.push(label);
	}
	if (labels.length === 0) return null;
	return labels.map((name, idx) => (
		<Box key={idx}>
			<Badge colorScheme={colorScheme}>{name}</Badge>
		</Box>
	));
}

function PlainHtml({ html }) {
	if (!html) return null;
	return <Box dangerouslySetInnerHTML={{ __html: html }} />;
}

function TransferNoteOfPresentation() {
	const toast = useToast();
	const location = useLocation();
	const modalUser = useDisclosure();
	const urlParams = new URLSearchParams(location.search);
	const id = urlParams.get('id') || null;
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const client = useApolloClient();

	const [transferee, setTransferee] = useState(null);
	const [options, setOptions] = useState([]);
	const [department, setDepartment] = useState('');
	const [office, setOffice] = useState('');
	const [notification, setNotification] = useState('none');
	const [list, setList] = useState([]);

	const [assigning, setAssigning] = useState(false);
	const [progress, setProgress] = useState({ done: 0, success: 0, duplicated: 0, failed: 0 });
	const [failed, setFailed] = useState([]);
	const cancelRef = useRef(false);

	const [fetchUser, { data: dataUser }] = useLazyQuery(Q_USER);
	const [fetchNps, { data: dataNps, loading: loadingNps }] = useLazyQuery(Q_NPS);

	useEffect(() => id && fetchUser({ variables: { id } }), [id]);

	useEffect(() => {
		if (!transferee || !dataUser?.getUserById?.id) return;
		fetchNps({
			variables: {
				assigneeId: dataUser.getUserById.id,
				isAssistantAssignment: options.includes('isAssistantAssignment') ? true : null,
				department: department || null,
				office: office || null,
				transfereeId: transferee?.id || null,
			},
		});
	}, [options, transferee, department, office, dataUser?.getUserById?.id]);

	useEffect(() => {
		setList(dataNps?.getAllNoteOfPresentationsAssignedToAssigneeId || []);
	}, [dataNps]);

	const total = list.length;
	const preview = useMemo(() => list.slice(0, PREVIEW_LIMIT), [list]);

	const runTransfer = useCallback(
		async (isAssistantAssignment) => {
			if (!transferee || list.length === 0 || assigning) return;
			cancelRef.current = false;
			setAssigning(true);
			setProgress({ done: 0, success: 0, duplicated: 0, failed: 0 });
			setFailed([]);

			const items = list;
			const total = items.length;
			const assigneeId = transferee.id;
			const notify = notification === 'all';

			let cursor = 0;
			let done = 0;
			let success = 0;
			let duplicated = 0;
			let failedCount = 0;
			const failedBuf = [];
			let lastFlush = 0;

			const flush = (force) => {
				const now = Date.now();
				if (!force && now - lastFlush < 100) return;
				lastFlush = now;
				setProgress({ done, success, duplicated, failed: failedCount });
				if (failedBuf.length) {
					const next = failedBuf.splice(0, failedBuf.length);
					setFailed((prev) => (prev.length >= FAILED_LIMIT ? prev : prev.concat(next).slice(0, FAILED_LIMIT)));
				}
			};

			async function worker() {
				while (!cancelRef.current) {
					const i = cursor++;
					if (i >= total) return;
					const item = items[i];
					try {
						const { data } = await client.mutate({
							mutation: M_ASSIGN,
							variables: {
								npId: item.id,
								assigneeId,
								isTransferAssistantAssignment: isAssistantAssignment === true,
								notification: notify,
							},
							fetchPolicy: 'no-cache',
						});
						const status = data?.assignNoteOfPresentationToAssigneeId;
						if (status === 'success') success++;
						else if (status === 'duplicated') duplicated++;
						else {
							failedCount++;
							failedBuf.push({ item, error: status || 'unknown' });
						}
					} catch (err) {
						failedCount++;
						failedBuf.push({ item, error: err?.message || 'error' });
					}
					done++;
					flush(false);
				}
			}

			const workers = [];
			const n = Math.min(CONCURRENCY, total);
			for (let i = 0; i < n; i++) workers.push(worker());
			await Promise.all(workers);
			flush(true);

			setAssigning(false);
			toast({
				title: cancelRef.current ? t('Cancelled') : t('Completed'),
				description: `${success} success / ${duplicated} duplicated / ${failedCount} failed`,
				status: cancelRef.current ? 'warning' : 'info',
				isClosable: true,
				position: 'bottom-right',
			});

			if (!cancelRef.current && failedCount === 0) {
				fetchNps({
					variables: {
						assigneeId: dataUser.getUserById.id,
						isAssistantAssignment: options.includes('isAssistantAssignment') ? true : null,
						department: department || null,
						office: office || null,
						transfereeId: transferee?.id || null,
					},
				});
			}
		},
		[transferee, list, assigning, notification, client, t, toast, dataUser, options, department, office],
	);

	const retryOne = useCallback(
		async (item, isAssistantAssignment) => {
			try {
				const { data } = await client.mutate({
					mutation: M_ASSIGN,
					variables: {
						npId: item.id,
						assigneeId: transferee.id,
						isTransferAssistantAssignment: isAssistantAssignment === true,
						notification: notification === 'all',
					},
					fetchPolicy: 'no-cache',
				});
				const status = data?.assignNoteOfPresentationToAssigneeId;
				if (status === 'success' || status === 'duplicated') {
					setFailed((prev) => prev.filter((f) => f.item.id !== item.id));
					setProgress((p) => ({
						...p,
						success: status === 'success' ? p.success + 1 : p.success,
						duplicated: status === 'duplicated' ? p.duplicated + 1 : p.duplicated,
						failed: Math.max(0, p.failed - 1),
					}));
					return true;
				}
				return false;
			} catch {
				return false;
			}
		},
		[client, transferee, notification],
	);

	const percent = total > 0 ? Math.floor((progress.done / total) * 100) : 0;
	const userDisplay = (u) =>
		lang === 'kh' ? u?.fullNameKhmer || u?.fullName || u?.username : u?.fullName || u?.fullNameKhmer || u?.username;

	return (
		<>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{ name: t('Users'), path: '/users?page=1' },
						{ name: t('Transfer Note of Presentation'), path: '#' },
					]}
				/>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex
							bg="#FAFAFA"
							p="16px"
							pt="8px"
							pb="8px"
							mb="16px"
							justifyContent="space-between"
							borderBottom="1px solid #dbdbdb"
						>
							<Text fontSize="x-large" fontWeight="bold">
								{t('Transfer Note of Presentation')}
							</Text>
						</Flex>
						{dataUser?.getUserById?.id && (
							<Box p="16px">
								<Flex justifyContent="center" alignItems="center" width="100%">
									<Flex w="150px" justifyContent="center" alignItems="center" direction="column">
										<Avatar
											name={userDisplay(dataUser?.getUserById)}
											src={dataUser?.getUserById?.avatar?.icon ? c.baseFilePath + dataUser?.getUserById?.avatar?.icon : null}
										/>
										<Text fontWeight="bold" mt={1}>
											{userDisplay(dataUser?.getUserById)}
										</Text>
									</Flex>
									<FontAwesomeIcon
										icon={faLongArrowAltRight}
										style={{ fontSize: 32, marginLeft: 32, marginRight: 32, color: '#2980b9' }}
									/>
									<Flex w="150px" justifyContent="center" alignItems="center" direction="column">
										{transferee ? (
											<>
												<WrapItem className="avatarWraper">
													{!assigning && (
														<FontAwesomeIcon
															onMouseDown={() => setTransferee(null)}
															className="remove"
															icon={faMinusSquare}
															style={{
																fontSize: 18,
																display: 'none',
																position: 'absolute',
																zIndex: '100',
																marginLeft: 32,
																color: 'red',
																backgroundColor: 'white',
																padding: 2,
																cursor: 'pointer',
															}}
														/>
													)}
													<Avatar
														name={userDisplay(transferee)}
														src={transferee?.avatar?.icon ? c.baseFilePath + transferee?.avatar?.icon : null}
													/>
												</WrapItem>
												<Text fontWeight="bold" mt={1}>
													{userDisplay(transferee)}
												</Text>
											</>
										) : (
											<Button
												onMouseDown={modalUser?.onOpen}
												colorScheme="blue"
												variant="outline"
												leftIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 18 }} />}
											>
												{t('Select')}
											</Button>
										)}
									</Flex>
								</Flex>

								{transferee && (
									<Flex mt="4" justifyContent="center" alignItems="center" direction="column" width="100%">
										<Stack spacing={4} direction="column" w="100%" maxW="520px">
											<Select
												size="sm"
												value={department}
												onChange={(e) => setDepartment(e.target.value)}
												placeholder={t('Choose Department')}
												isDisabled={assigning}
											>
												{c.departments.map((item, index) => (
													<option key={index} value={item?.value}>
														{item?.labelKh}
													</option>
												))}
											</Select>
											<Select
												size="sm"
												value={office}
												onChange={(e) => setOffice(e.target.value)}
												placeholder={t('Choose Office')}
												isDisabled={assigning}
											>
												{c.departments
													?.find((d) => d.value === department)
													?.offices.map((item, index) => (
														<option key={index} value={item?.value}>
															{item?.labelKh}
														</option>
													))}
											</Select>
											<CheckboxGroup isDisabled={assigning} colorScheme="blue" defaultValue={options} onChange={setOptions}>
												<Stack spacing={2} direction="column">
													<Checkbox value="isAssistantAssignment">{t('Only Assistant Assignments')}</Checkbox>
												</Stack>
											</CheckboxGroup>
											<Divider />

											<HStack justify="space-between">
												<Text fontWeight="500">{t('Matched')}</Text>
												<Badge colorScheme="orange" fontSize="md">
													{loadingNps ? '…' : total}
												</Badge>
											</HStack>

											{total > 0 && (
												<HStack spacing={4}>
													<Text fontWeight="500" fontSize="md">
														{t('Notification')}
													</Text>
													<RadioGroup isDisabled={assigning} value={notification} onChange={setNotification}>
														<HStack spacing={4}>
															<Radio value="none">{t('None')}</Radio>
															<Radio value="all">{t('All')}</Radio>
														</HStack>
													</RadioGroup>
												</HStack>
											)}

											<Button
												onMouseDown={() => runTransfer(false)}
												colorScheme="blue"
												variant="solid"
												isDisabled={!(total > 0) || assigning}
											>
												{t('Transfer')} {total > 0 && total}
											</Button>
											<Button
												onMouseDown={() => {
													const roles = transferee?.role?.split(',') || [];
													if (!roles.includes('assistant')) {
														toast({
															title: t('Not allowed, transferee is not assistant'),
															status: 'warning',
															isClosable: true,
															position: 'bottom',
														});
														return;
													}
													runTransfer(true);
												}}
												colorScheme="green"
												variant="solid"
												isDisabled={!(total > 0) || assigning}
											>
												{t('Transfer as Assistant Assignments')} {total > 0 && total}
											</Button>

											{total === 0 && !loadingNps && (
												<Text fontWeight="500" fontSize="md" color="orange.500">
													{t('No np or rp to assign')}
												</Text>
											)}

											{(assigning || progress.done > 0) && (
												<Box>
													<Progress value={percent} size="sm" colorScheme="blue" borderRadius="md" />
													<HStack mt={2} spacing={4} fontSize="sm">
														<Text>
															{progress.done}/{total} ({percent}%)
														</Text>
														<Badge colorScheme="green">✓ {progress.success}</Badge>
														<Badge colorScheme="yellow">≈ {progress.duplicated}</Badge>
														<Badge colorScheme="red">✗ {progress.failed}</Badge>
														{assigning && (
															<Button
																size="xs"
																colorScheme="red"
																variant="outline"
																onMouseDown={() => {
																	cancelRef.current = true;
																}}
															>
																{t('Cancel')}
															</Button>
														)}
													</HStack>
												</Box>
											)}
										</Stack>
									</Flex>
								)}

								<Divider mt={6} mb={6} />

								{transferee && failed.length > 0 && (
									<Box mb={6}>
										<Text fontWeight="bold" mb={2} color="red.600">
											{t('Failed')} ({failed.length}
											{failed.length >= FAILED_LIMIT ? '+' : ''})
										</Text>
										<Stack spacing={1}>
											{failed.map((f) => (
												<TransferNoteOfPresentationItem
													key={f.item.id}
													item={f.item}
													error={f.error}
													onRetry={() => retryOne(f.item, false)}
												/>
											))}
										</Stack>
									</Box>
								)}

								{transferee && total > 0 && !assigning && progress.done === 0 && (
									<Box>
										<Text fontWeight="500" mb={2}>
											{t('Preview')} ({Math.min(total, PREVIEW_LIMIT)}/{total})
										</Text>
										<Stack spacing={2}>
											{preview.map((item) => (
												<Box
													key={item.id}
													borderColor="gray.300"
													borderWidth="1px"
													borderRadius={8}
													borderStyle="dotted"
													p={3}
												>
													<HStack spacing={3} align="flex-start">
														<Stack spacing={1}>
															<Badge colorScheme="orange">{item?.number ?? 'N/A'}</Badge>
															<Badge colorScheme="green">{t(item?.type)}</Badge>
															{joinedBadges(item?.department, DEPT_LABEL, 'blue')}
															{joinedBadges(item?.office, OFFICE_LABEL, 'purple')}
														</Stack>
														<Box flex="1" fontSize="sm">
															<PlainHtml html={item?.subject} />
														</Box>
													</HStack>
												</Box>
											))}
											{total > PREVIEW_LIMIT && (
												<Text fontSize="sm" color="gray.500">
													+ {total - PREVIEW_LIMIT} {t('more')}
												</Text>
											)}
										</Stack>
									</Box>
								)}
							</Box>
						)}
					</Box>
				</Center>
			</Container>
			<SelectUserModal
				role="companySubmissionProposal"
				isOpen={modalUser?.isOpen}
				callback={(user) => {
					if (dataUser?.getUserById?.id !== user?.id) {
						setTransferee(user);
					} else {
						toast({
							title: t('Not Allowed'),
							status: 'warning',
							isClosable: true,
							position: 'bottom',
						});
					}
				}}
				onClose={modalUser?.onClose}
			/>
		</>
	);
}

export default TransferNoteOfPresentation;
